import { lazy, type ComponentType } from 'react';

declare const __SW_VERSION__: string;

/**
 * A screen fetched when it is first opened rather than with the app.
 *
 * Everything used to come down as one file, so the screens hardly anybody
 * opens -- the admin page, search, settings -- were paid for on every opening.
 * The service worker still keeps them all, so one opened offline is there.
 */
export function lazyScreen<M, P>(load: () => Promise<M>, pick: (module: M) => ComponentType<P>) {
  return lazy(() =>
    load().then(
      (module) => ({ default: pick(module) }),
      (error: unknown) => reloadOnce(error),
    ),
  );
}

const KEY = 'givisnap:chunkReload';

/**
 * A piece that will not load is almost always a page built against a version
 * that has since been replaced: the deploy took its files away. Reloading
 * brings the new version and the pieces that go with it. Only once a version,
 * so a piece that is truly missing ends at the crash screen and its Reload,
 * not in a loop.
 */
export function reloadOnce(
  error: unknown,
  storage: Pick<Storage, 'getItem' | 'setItem'> = sessionStorage,
  reload: () => void = () => window.location.reload(),
  build: string = typeof __SW_VERSION__ === 'string' ? __SW_VERSION__ : 'unknown',
): Promise<never> {
  try {
    if (storage.getItem(KEY) !== build) {
      storage.setItem(KEY, build);
      reload();
      // The page is going; nothing should draw in the meantime.
      return new Promise<never>(() => undefined);
    }
  } catch {
    // No storage to remember it by: better the crash screen than a loop.
  }
  return Promise.reject(error);
}
